import { c as _c } from "react/compiler-runtime";
import Link from "next/link";
import { ArrowRight, BookOpen, Coins, Feather, Globe2, Swords, type LucideIcon } from "lucide-react";
import { ARTICLE_TOPICS, getArticleTopic, type ArticleTopicKey } from "@/lib/articles/topics";
import { ArticleLink } from "@/components/articles/article-link";

// Shared shape the /articles index passes to every card variant.
export interface ArticleListItem {
  slug: string;
  title: string;
  excerpt: string | null;
  authorByline: string;
  publishedAt: Date | null;
  readMinutes: number;
  topic: ArticleTopicKey;
}

// Per-topic visual identity: icon + brand-family gradient. Articles have no
// banner images, so these tiles carry the visual weight of the cards.
const TOPIC_VISUALS: Record<ArticleTopicKey, {
  icon: LucideIcon;
  gradient: string;
  dot: string;
}> = {
  craft: {
    icon: Feather,
    gradient: "from-[#6C5CE7] to-[#A29BFE]",
    dot: "bg-[#A29BFE]"
  },
  progression: {
    icon: Swords,
    gradient: "from-[#7C3AED] to-[#FF7675]",
    dot: "bg-[#FF7675]"
  },
  worldbuilding: {
    icon: Globe2,
    gradient: "from-[#4834D4] to-[#686DE0]",
    dot: "bg-[#686DE0]"
  },
  business: {
    icon: Coins,
    gradient: "from-[#FF7675] to-[#FD79A8]",
    dot: "bg-[#FD79A8]"
  },
  guides: {
    icon: BookOpen,
    gradient: "from-[#30336B] to-[#6C5CE7]",
    dot: "bg-[#6C5CE7]"
  }
};
function formatDate(date: Date | null): string | null {
  if (!date) return null;
  return new Date(date).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric"
  });
}
function TopicIconTile(t0) {
  const $ = _c(5);
  const {
    topic,
    className
  } = t0;
  const {
    icon: Icon,
    gradient
  } = TOPIC_VISUALS[topic];
  const t1 = `flex items-center justify-center rounded-xl bg-gradient-to-br ${gradient} text-white shadow-md ${className ?? "size-10"}`;
  let t2;
  if ($[0] !== Icon) {
    t2 = <Icon className="size-[55%]" strokeWidth={1.75} />;
    $[0] = Icon;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== t1 || $[3] !== t2) {
    t3 = <div className={t1}>{t2}</div>;
    $[2] = t1;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
}
function ArticleMeta(t0) {
  const $ = _c(13);
  const {
    item
  } = t0;
  let t1;
  if ($[0] !== item.publishedAt) {
    t1 = formatDate(item.publishedAt);
    $[0] = item.publishedAt;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const dateStr = t1;
  let t2;
  if ($[2] !== item.authorByline) {
    t2 = <span className="font-medium text-foreground/80">{item.authorByline}</span>;
    $[2] = item.authorByline;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== dateStr) {
    t3 = dateStr && <><span aria-hidden={true}>·</span><span>{dateStr}</span></>;
    $[4] = dateStr;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <span aria-hidden={true}>·</span>;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== item.readMinutes) {
    t5 = <span>{item.readMinutes} min read</span>;
    $[7] = item.readMinutes;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t2 || $[10] !== t3 || $[11] !== t5) {
    t6 = <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">{t2}{t3}{t4}{t5}</div>;
    $[9] = t2;
    $[10] = t3;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  return t6;
}

/** Filter chips: All + one per topic. Server-rendered links, no client JS. */
export function ArticleTopicChips(t0) {
  const $ = _c(7);
  const {
    activeTopic
  } = t0;
  const t1 = `${"inline-flex items-center rounded-full border px-4 py-1.5 text-sm font-medium transition-colors"} ${activeTopic === null ? "border-transparent bg-primary text-primary-foreground shadow-md shadow-primary/25" : "border-border bg-card/60 text-muted-foreground hover:border-primary/40 hover:text-foreground"}`;
  let t2;
  if ($[0] !== t1) {
    t2 = <Link href="/articles" className={t1}>All</Link>;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== activeTopic) {
    t3 = ARTICLE_TOPICS.map(topic => <Link key={topic.key} href={`/articles?topic=${topic.key}`} className={`${"inline-flex items-center rounded-full border px-4 py-1.5 text-sm font-medium transition-colors"} ${activeTopic === topic.key ? "border-transparent bg-primary text-primary-foreground shadow-md shadow-primary/25" : "border-border bg-card/60 text-muted-foreground hover:border-primary/40 hover:text-foreground"}`}>{topic.label}</Link>);
    $[2] = activeTopic;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t2 || $[5] !== t3) {
    t4 = <nav aria-label="Article topics" className="flex flex-wrap items-center gap-2">{t2}{t3}</nav>;
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  return t4;
}

/** Large editorial hero for the newest article. */
export function ArticleHeroCard(t0) {
  const $ = _c(27);
  const {
    item
  } = t0;
  let t1;
  if ($[0] !== item.topic) {
    t1 = getArticleTopic(item.topic);
    $[0] = item.topic;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const topic = t1;
  const {
    icon: Icon,
    gradient
  } = TOPIC_VISUALS[item.topic];
  const t2 = `/articles/${item.slug}`;
  let t3;
  let t4;
  let t5;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-brand/10 via-transparent to-accent/10" />;
    t4 = <div className="pointer-events-none absolute -top-20 -right-16 size-72 rounded-[55%_45%_60%_40%] bg-brand/20 blur-[90px]" />;
    t5 = <div className="pointer-events-none absolute -bottom-16 right-1/4 size-56 rounded-[45%_55%_40%_60%] bg-accent/15 blur-[80px]" />;
    $[2] = t3;
    $[3] = t4;
    $[4] = t5;
  } else {
    t3 = $[2];
    t4 = $[3];
    t5 = $[4];
  }
  let t6;
  if ($[5] !== Icon) {
    t6 = <Icon aria-hidden={true} className="pointer-events-none absolute -right-6 -bottom-8 size-52 -rotate-12 text-primary/10 transition-transform duration-300 group-hover:-rotate-6 sm:size-64" strokeWidth={1} />;
    $[5] = Icon;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <span className="inline-flex items-center rounded-full bg-accent/15 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-accent">Latest</span>;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  const t8 = `inline-flex items-center rounded-full bg-gradient-to-r ${gradient} px-3 py-1 text-xs font-semibold text-white`;
  let t9;
  if ($[8] !== t8 || $[9] !== topic.label) {
    t9 = <div className="mb-4 flex flex-wrap items-center gap-2">{t7}<span className={t8}>{topic.label}</span></div>;
    $[8] = t8;
    $[9] = topic.label;
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  let t10;
  if ($[11] !== item.title) {
    t10 = <h2 className="font-heading mb-4 text-2xl font-extrabold tracking-tight text-foreground transition-colors group-hover:text-primary sm:text-4xl">{item.title}</h2>;
    $[11] = item.title;
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  let t11;
  if ($[13] !== item.excerpt) {
    t11 = item.excerpt && <p className="mb-6 leading-relaxed text-muted-foreground line-clamp-3">{item.excerpt}</p>;
    $[13] = item.excerpt;
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  let t12;
  if ($[15] !== item) {
    t12 = <div className="mb-8"><ArticleMeta item={item} /></div>;
    $[15] = item;
    $[16] = t12;
  } else {
    t12 = $[16];
  }
  let t13;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <span className="inline-flex items-center gap-2 rounded-full bg-brand px-6 py-2.5 text-sm font-semibold text-white shadow-lg shadow-brand/30 transition-colors group-hover:bg-brand/90">Read article<ArrowRight className="size-4 transition-transform group-hover:translate-x-0.5" /></span>;
    $[17] = t13;
  } else {
    t13 = $[17];
  }
  let t14;
  if ($[18] !== t10 || $[19] !== t11 || $[20] !== t12 || $[21] !== t9) {
    t14 = <div className="relative max-w-2xl p-8 sm:p-12">{t9}{t10}{t11}{t12}{t13}</div>;
    $[18] = t10;
    $[19] = t11;
    $[20] = t12;
    $[21] = t9;
    $[22] = t14;
  } else {
    t14 = $[22];
  }
  let t15;
  if ($[23] !== t14 || $[24] !== t2 || $[25] !== t6) {
    t15 = <ArticleLink href={t2} className="group relative block overflow-hidden rounded-3xl border border-border bg-card transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_10px_30px_rgba(108,92,231,0.15),0_4px_12px_rgba(0,0,0,0.1)]">{t3}{t4}{t5}{t6}{t14}</ArticleLink>;
    $[23] = t14;
    $[24] = t2;
    $[25] = t6;
    $[26] = t15;
  } else {
    t15 = $[26];
  }
  return t15;
}

/** Standard grid card. */
export function ArticleCard(t0) {
  const $ = _c(21);
  const {
    item
  } = t0;
  let t1;
  if ($[0] !== item.topic) {
    t1 = getArticleTopic(item.topic);
    $[0] = item.topic;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const topic = t1;
  const t2 = `/articles/${item.slug}`;
  let t3;
  if ($[2] !== item.topic) {
    t3 = <TopicIconTile topic={item.topic} />;
    $[2] = item.topic;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== topic.label) {
    t4 = <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{topic.label}</span>;
    $[4] = topic.label;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t3 || $[7] !== t4) {
    t5 = <div className="mb-4 flex items-center gap-3">{t3}{t4}</div>;
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== item.title) {
    t6 = <h3 className="mb-2 text-lg font-bold leading-snug text-foreground transition-colors group-hover:text-primary line-clamp-2">{item.title}</h3>;
    $[9] = item.title;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== item.excerpt) {
    t7 = item.excerpt && <p className="mb-4 text-sm leading-relaxed text-muted-foreground line-clamp-2">{item.excerpt}</p>;
    $[11] = item.excerpt;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== item) {
    t8 = <div className="mt-auto"><ArticleMeta item={item} /></div>;
    $[13] = item;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== t2 || $[16] !== t5 || $[17] !== t6 || $[18] !== t7 || $[19] !== t8) {
    t9 = <ArticleLink href={t2} className="group flex h-full flex-col rounded-2xl border border-border bg-card p-6 transition-all duration-300 hover:-translate-y-1 hover:border-primary/30 hover:shadow-lg">{t5}{t6}{t7}{t8}</ArticleLink>;
    $[15] = t2;
    $[16] = t5;
    $[17] = t6;
    $[18] = t7;
    $[19] = t8;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  return t9;
}

/** Dense list row for the full archive feed. */
export function ArticleRow(t0) {
  const $ = _c(25);
  const {
    item
  } = t0;
  let t1;
  if ($[0] !== item.publishedAt) {
    t1 = formatDate(item.publishedAt);
    $[0] = item.publishedAt;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const dateStr = t1;
  const {
    dot
  } = TOPIC_VISUALS[item.topic];
  const t2 = `/articles/${item.slug}`;
  const t3 = `size-2 shrink-0 translate-y-[-1px] rounded-full ${dot}`;
  let t4;
  if ($[2] !== t3) {
    t4 = <span aria-hidden={true} className={t3} />;
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== item.title) {
    t5 = <h3 className="font-semibold text-foreground transition-colors group-hover:text-primary line-clamp-1">{item.title}</h3>;
    $[4] = item.title;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== t4 || $[7] !== t5) {
    t6 = <div className="flex items-baseline gap-2.5">{t4}{t5}</div>;
    $[6] = t4;
    $[7] = t5;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== item.excerpt) {
    t7 = item.excerpt && <p className="mt-1 pl-[18px] text-sm text-muted-foreground line-clamp-1">{item.excerpt}</p>;
    $[9] = item.excerpt;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== t6 || $[12] !== t7) {
    t8 = <div className="min-w-0">{t6}{t7}</div>;
    $[11] = t6;
    $[12] = t7;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== dateStr) {
    t9 = dateStr && <div>{dateStr}</div>;
    $[14] = dateStr;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== item.readMinutes) {
    t10 = <div>{item.readMinutes} min</div>;
    $[16] = item.readMinutes;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== t10 || $[19] !== t9) {
    t11 = <div className="shrink-0 text-right text-xs text-muted-foreground">{t9}{t10}</div>;
    $[18] = t10;
    $[19] = t9;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== t11 || $[22] !== t2 || $[23] !== t8) {
    t12 = <ArticleLink href={t2} className="group flex items-baseline justify-between gap-6 border-b border-border/60 py-4 transition-colors hover:border-primary/40">{t8}{t11}</ArticleLink>;
    $[21] = t11;
    $[22] = t2;
    $[23] = t8;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  return t12;
}

/** Author-acquisition CTA band ("you keep what you earn"). */
export function WriterCtaBand() {
  const $ = _c(5);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="pointer-events-none absolute -top-16 -left-10 size-64 rounded-[60%_40%_55%_45%] bg-accent/30 blur-[80px]" />;
    t1 = <div className="pointer-events-none absolute -bottom-12 -right-12 size-56 rounded-[45%_55%_40%_60%] bg-white/10 blur-[70px]" />;
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  let t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <h2 className="font-heading mb-3 text-2xl font-extrabold tracking-tight text-white sm:text-3xl">You do the work, you keep what you earn.</h2>;
    t3 = <p className="mb-7 leading-relaxed text-white/85">Publish your web fiction on IlorisNovel — writing, world building, and earnings in one place, and you keep 93% of what your readers pay.</p>;
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <section className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-brand to-brand-light px-8 py-12 sm:px-12">{t0}{t1}<div className="relative max-w-2xl">{t2}{t3}<div className="flex flex-wrap items-center gap-3"><Link href="/signup/writer" className="inline-flex items-center gap-2 rounded-full bg-accent px-6 py-2.5 text-sm font-semibold text-white shadow-lg shadow-accent/30 transition-colors hover:bg-accent/90">Start Writing for Free<ArrowRight className="size-4" /></Link><Link href="/try" className="inline-flex items-center rounded-full border border-white/40 px-6 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-white/10">Try the editor — no account</Link></div></div></section>;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  return t4;
}